import { Badge, Button, Card, PageHeader, Skeleton } from '@zyxantech/tux'
import { Building2, Users } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useGetCustomersQuery } from '@/api/partners.api'
import { EmptyState } from '@/components/shared/EmptyState'
import { ErrorState } from '@/components/shared/ErrorState'
import { ROUTES } from '@/constants/routes'
import { formatTimestamp } from '@/utils/format'
import { useMyPartner } from './hooks/useMyPartner'

export function PartnerOverviewPage() {
  const navigate = useNavigate()
  const { partner, isLoading, error } = useMyPartner()
  const { data: customersData } = useGetCustomersQuery(
    { id: partner?.id ?? '' },
    { skip: !partner },
  )

  if (isLoading) {
    return (
      <div className="space-y-4 p-6">
        <Skeleton height={28} width="30%" />
        <Skeleton height={200} />
      </div>
    )
  }
  if (error) {
    return (
      <div className="p-6">
        <ErrorState error={error} />
      </div>
    )
  }
  if (!partner) {
    return (
      <div className="p-6">
        <EmptyState title="No partner found" message="Your account is not associated with a partner." />
      </div>
    )
  }

  const customerCount = customersData?.pagination.total ?? 0

  return (
    <div className="p-6">
      <PageHeader
        title="Partner Portal"
        subtitle="Manage your partner account and customer tenants"
      />

      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        {/* Partner info card */}
        <Card>
          <div className="flex items-start gap-3">
            <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-accent/10">
              <Building2 size={20} className="text-accent" />
            </div>
            <div className="min-w-0">
              <h2 className="font-semibold text-text-primary">{partner.name}</h2>
              <p className="font-mono text-xs text-text-muted">{partner.slug}</p>
            </div>
            <Badge variant={partner.status === 'active' ? 'success' : 'default'}>
              {partner.status}
            </Badge>
          </div>

          <div className="mt-4 space-y-2 border-t border-border-subtle pt-4 text-sm">
            <Row label="Contact" value={partner.contactEmail ?? '—'} />
            <Row label="Since" value={formatTimestamp(partner.createdAt)} />
          </div>
        </Card>

        {/* Customers summary card */}
        <Card>
          <div className="flex items-start gap-3">
            <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-info/10">
              <Users size={20} className="text-info" />
            </div>
            <div>
              <h2 className="font-semibold text-text-primary">Customers</h2>
              <p className="text-sm text-text-muted">Tenant accounts you manage</p>
            </div>
          </div>

          <div className="mt-4 flex items-end justify-between border-t border-border-subtle pt-4">
            <p className="text-3xl font-bold tabular-nums text-text-primary">{customerCount}</p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(ROUTES.CUSTOMERS)}
            >
              View all
            </Button>
          </div>
        </Card>
      </div>
    </div>
  )
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex gap-2">
      <span className="w-20 shrink-0 text-text-muted">{label}</span>
      <span className="text-text-secondary">{value}</span>
    </div>
  )
}
